// KontaktStatusSchritt.tsx — der erste Schritt der Kontaktanlage (User 26.09.2026):
//
//   |Kunde|Interessent|Sonstige|
//   bei Sonstige: Zeilen |Bereich|Rolle| + „+ weitere Rolle"
//   |Firma|Privat|  — bei Sonstige aus den Rollen abgeleitet
//
// „Persönlicher Kontakt" steht seit 27.09.2026 nicht mehr im Status: Er ist
// „Persönlicher Inhouse Kontakt von" in der FO/BO-Karte (OwnershipSection).
//
// Gemeinsam für die Einstiegsseite (noch kein Typ) und die beiden Formulare.
// Die Felder sind dieselben wie bisher (`kontaktStatus`, `persoenlich`,
// `kontaktStatusRollen`, `kontaktStatusAuch`, `kontaktStatusBezuege`); der
// Payload-Weg bleibt unverändert. Folgefelder (Unternehmen, Verein, Person mit
// Beziehung) setzt der Aufrufer über `folgefelder` direkt unter die Rollen.

import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";
import { StatusSegmented } from "./StatusSegmented";
import { LabeledSelect } from "./formPrimitives";
import { MehrfachSelect } from "./widgets/MehrfachSelect";
import { StatusBezuege, bezugsStand, bezuegeAusFeld, bezuegeInsFeld } from "./widgets/StatusBezuege";
import { GegenueberRahmen } from "./widgets/GegenueberKasten";
import { folgeKarteOffen, folgeKarten } from "../../data/gegenueberFolgen";
import {
  STATUS_SONSTIGE,
  oberSegmentAuswahl,
  rollenAusFeld,
  rollenInsFeld,
} from "../../data/kontaktstatusSegment";
import {
  alsStatusEintraege,
  bereichVon,
  bereicheVon,
  folgenAus,
  passtZuTyp,
  rolleZu,
  sichtbareBereiche,
  typAusRollen,
  type RollenFolge,
} from "../../data/kontaktRollen";
import { useKontaktRollen } from "../../services/useKontaktRollen";

export type KontaktTyp = "person" | "firma";

const useStyles = makeStyles({
  schritt: { display: "flex", flexDirection: "column", rowGap: "14px" },
  block: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    paddingLeft: "14px",
    ...shorthands.borderLeft("2px", "solid", REFINED.aub200),
  },
  frage: { fontFamily: REFINED.fontUI, fontSize: "0.875rem", fontWeight: 600, color: REFINED.ink },
  zeile: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr) auto",
    columnGap: "12px",
    alignItems: "end",
    "@media (max-width: 700px)": { gridTemplateColumns: "minmax(0, 1fr)", rowGap: "8px" },
  },
  weg: {
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.ink3,
    cursor: "pointer",
    fontSize: "1.1rem",
    ...shorthands.padding("8px"),
    ...shorthands.borderRadius("8px"),
    ":hover": { color: REFINED.err, backgroundColor: REFINED.errBg },
  },
  // Textknopf wie „+ weitere Branche" (BrancheZweiSelect.linkBtn).
  plus: {
    appearance: "none",
    alignSelf: "flex-start",
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.aub,
    cursor: "pointer",
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    fontWeight: 600,
    ...shorthands.padding("0"),
    ":hover": { textDecorationLine: "underline" },
  },
  hinweis: { fontFamily: REFINED.fontUI, fontSize: "0.8rem", color: REFINED.ink3 },
  typLink: {
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer",
    ...shorthands.padding("0"),
    textDecorationLine: "underline",
  },
});

const TYP_LABEL: Record<KontaktTyp, string> = { firma: "Firma", person: "Privat" };

/** Reihenfolge laut User 27.09.2026 — Kunde zuerst. */
const ANLAGE_STATUS = ["Kunde", "Interessent", STATUS_SONSTIGE];
/** Wortlaut laut User 28.09.2026 — „Firma | Privat" statt „Unternehmen | Privat". */
const TYP_FRAGE = "Handelt es sich um eine Firma oder um eine Privatperson?";

export function KontaktStatusSchritt({
  f,
  set,
  typ,
  onTyp,
  convert = false,
  coIntern,
  folgefelder,
  ohneBezuege = false,
  children,
}: {
  f: (k: string) => string;
  set: (k: string, v: string) => void;
  /** Gewählter bzw. festgelegter Kontakttyp (im Formular fest). */
  typ: KontaktTyp | null;
  /**
   * Typ wählen (Einstieg) oder wechseln (Formular). Fehlt er, ist der Typ fest
   * — etwa beim Umwandeln.
   */
  onTyp?: (t: KontaktTyp) => void;
  convert?: boolean;
  /** Recht `cointern:read` — schaltet den CO-intern-Lieferanten frei. */
  coIntern: boolean;
  /** Folgefelder der gewählten Rollen, direkt unter der Rollenwahl. */
  folgefelder?: (folgen: RollenFolge[]) => React.ReactNode;
  /** Unter allem, z. B. der Dubletten-Hinweis. */
  children?: React.ReactNode;
  /**
   * Die Rückfrage „für wen?" weglassen — für Schnellanlagen, die die
   * Beziehungen nicht mitspeichern (Zuweisen-Dialog der Alten Kundenakten).
   * Sie wird dann in der Akte nachgetragen.
   */
  ohneBezuege?: boolean;
}) {
  const s = useStyles();
  // Katalog aus den Einstellungen (Fallback: eingebaut) — die Funktionen aus
  // data/kontaktRollen arbeiten mit ihm; `katalog` ist nur der Render-Auslöser.
  const katalog = useKontaktRollen();
  const kontaktStatus = f("kontaktStatus") || "Interessent";
  const istSonstige = kontaktStatus === STATUS_SONSTIGE;
  const rollen = rollenAusFeld(f("kontaktStatusRollen"));
  const abgeleitet = istSonstige ? typAusRollen(rollen) : null;
  const typAusRolle = abgeleitet === "person" || abgeleitet === "firma" ? abgeleitet : null;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bereiche = React.useMemo(() => sichtbareBereiche(coIntern), [coIntern, katalog]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const eintraege = React.useMemo(() => alsStatusEintraege(), [katalog]);

  // Zeilen = Bereiche der gewählten Rollen (in Katalogreihenfolge) plus die
  // noch leeren, die „+ Zusätzliche Rolle" angelegt hat. Die leeren sind reine
  // Ansicht und gehören nicht in den Formular-State.
  const [leereZeilen, setLeereZeilen] = React.useState<string[]>([]);
  // Steht eine Rolle in mehreren Bereichen (z. B. „Halter"), bleibt sie in
  // der Zeile, in der sie gewählt wurde — gespeichert wird ohnehin nur der
  // Status; ohne Wahl gilt der erste Bereich des Katalogs.
  const [bereichWahl, setBereichWahl] = React.useState<Record<string, string>>({});
  const bereichFuer = (r: string): string | undefined =>
    bereichWahl[r] && bereicheVon(r).includes(bereichWahl[r]) ? bereichWahl[r] : bereichVon(r);
  const belegteBereiche = bereiche
    .map((b) => b.titel)
    .filter((t) => rollen.some((r) => bereichFuer(r) === t));
  // Altbestand (Rollen, die der neue Katalog nicht kennt) bleibt sichtbar.
  const altRollen = rollen.filter((r) => !bereichVon(r));
  const zeilen = [...belegteBereiche, ...leereZeilen.filter((t) => !belegteBereiche.includes(t))];
  React.useEffect(() => {
    // Beim ersten „Sonstige" gleich eine leere Zeile zeigen.
    if (istSonstige && rollen.length === 0 && leereZeilen.length === 0) setLeereZeilen([""]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [istSonstige]);

  const setzeRollen = (next: string[]) => {
    set("kontaktStatusRollen", rollenInsFeld(next));
    // Eine abgewählte Rolle nimmt ihren Bezug mit.
    set(
      "kontaktStatusBezuege",
      bezuegeInsFeld(bezuegeAusFeld(f("kontaktStatusBezuege")).filter((b) => next.includes(b.rolle))),
    );
  };

  const statusGewaehlt = (v: string) => {
    const auswahl = oberSegmentAuswahl(v, f("kontaktStatus"));
    set("kontaktStatus", auswahl.kontaktStatus);
    if (auswahl.kontaktStatus !== STATUS_SONSTIGE) {
      set("kontaktStatusRollen", "");
      set("kontaktStatusAuch", "");
      set("kontaktStatusBezuege", "");
      setLeereZeilen([]);
    }
  };

  const zeileFuer = (bereich: string, idx: number) => {
    const b = bereiche.find((x) => x.titel === bereich);
    const hier = rollen.filter((r) => bereichFuer(r) === bereich);
    const andereRollen = rollen.filter((r) => bereichFuer(r) !== bereich);
    // Typ, den die ANDEREN Zeilen (bzw. das Formular) vorgeben — innerhalb
    // der Zeile darf man zwischen Rollen desselben Typs frei wechseln.
    const typAndere = typ ?? (() => {
      const t = typAusRollen(andereRollen);
      return t === "person" || t === "firma" ? t : null;
    })();
    const typHier = typAusRollen(hier);
    const grenze = typAndere ?? (typHier === "person" || typHier === "firma" ? typHier : null);
    const belegtVonAnderen = new Set(zeilen.filter((_, i) => i !== idx));
    return (
      <div className={s.zeile} key={`${bereich}-${idx}`}>
        <LabeledSelect
          label="Bereich"
          name={`rollenBereich-${idx}`}
          value={bereich}
          onChange={(v) => {
            // Bereich gewechselt: seine Rollen gehen mit.
            setzeRollen(andereRollen);
            setLeereZeilen((alt) => {
              const ohne = alt.filter((t) => t !== bereich);
              return [...ohne, v];
            });
          }}
          options={bereiche
            .filter((x) => x.titel === bereich || !belegtVonAnderen.has(x.titel))
            .map((x) => ({ value: x.titel, label: x.titel }))}
          placeholder="Bereich wählen"
        />
        <MehrfachSelect<string>
          label="Rolle"
          name={`rollen-${idx}`}
          value={hier}
          disabled={!b}
          options={(b?.rollen ?? []).map((r) => {
            const passt = passtZuTyp(r.value, grenze) || hier.includes(r.value);
            return {
              value: r.value,
              label: r.label,
              gesperrt: !passt,
              hint: !passt ? (r.typ === "firma" ? "nur Firma" : "nur Privat") : undefined,
            };
          })}
          onToggle={(v) => {
            // Schon in einer anderen Zeile gewählt → hierher holen statt abwählen.
            if (hier.includes(v)) setzeRollen(rollen.filter((r) => r !== v));
            else if (!rollen.includes(v)) setzeRollen([...rollen, v]);
            setBereichWahl((alt) => ({ ...alt, [v]: bereich }));
          }}
          beschriftung={hier.map((r) => rolleZu(r)?.label ?? r).join(", ") || "Rolle wählen"}
        />
        <button
          type="button"
          className={s.weg}
          aria-label="Zeile entfernen"
          title="Zeile entfernen"
          onClick={() => {
            setzeRollen(andereRollen);
            setLeereZeilen((alt) => alt.filter((t) => t !== bereich));
          }}
        >
          ×
        </button>
      </div>
    );
  };

  const folgen = istSonstige ? folgenAus(rollen) : [];
  // Alle Rollen mit Gegenüber in EINEM Rahmen (User 02.10.2026): die mit
  // `bezug` (StatusBezuege) und die mit Folgefeldern — eine Einleitung, ein
  // Hinweis „Noch … offen" über beide hinweg.
  const bezuege = bezuegeAusFeld(f("kontaktStatusBezuege"));
  const bezugStand = ohneBezuege ? { anzahl: 0, offen: 0 } : bezugsStand(eintraege, rollen, bezuege);
  const folgeKaesten = folgefelder ? folgeKarten(folgen) : [];
  const istFirma = (typ ?? typAusRolle) === "firma";
  const folgenOffen = folgeKaesten.filter((k) => folgeKarteOffen(k, f, istFirma)).length;
  const sonstigeBlock = istSonstige ? (
    <div className={s.block}>
      {zeilen.map((b, i) => zeileFuer(b, i))}
      {altRollen.length > 0 && (
        <span className={s.hinweis}>Weitere Rollen aus dem Bestand: {altRollen.join(", ")}</span>
      )}
      <button
        type="button"
        className={s.plus}
        onClick={() => setLeereZeilen((alt) => (alt.includes("") ? alt : [...alt, ""]))}
      >
        + weitere Rolle
      </button>
      <GegenueberRahmen
        anzahl={bezugStand.anzahl + folgeKaesten.length}
        offen={bezugStand.offen + folgenOffen}
      >
        {folgen.length > 0 && folgefelder?.(folgen)}
        {!ohneBezuege && (
          <StatusBezuege
            eintraege={eintraege}
            rollen={rollen}
            bezuege={bezuege}
            onChange={(liste) => set("kontaktStatusBezuege", bezuegeInsFeld(liste))}
            ohneRahmen
          />
        )}
      </GegenueberRahmen>
      <span className={s.frage}>Ist der Kontakt auch Kunde oder Interessent?</span>
      <StatusSegmented
        value={f("kontaktStatusAuch")}
        options={["Kunde", "Interessent"]}
        onChange={(v) => set("kontaktStatusAuch", f("kontaktStatusAuch") === v ? "" : v)}
        ariaLabel="Ist der Kontakt auch Kunde oder Interessent?"
      />
    </div>
  ) : null;

  // Firma | Privat — bei festlegender Rolle vorausgewählt, aber wechselbar.
  const typBlock = (
    <div style={{ display: "flex", flexDirection: "column", rowGap: 6 }}>
      <span className={s.frage}>{TYP_FRAGE}</span>
      <StatusSegmented
        value={typ ? TYP_LABEL[typ] : typAusRolle ? TYP_LABEL[typAusRolle] : ""}
        options={[TYP_LABEL.firma, TYP_LABEL.person]}
        onChange={(v) => {
          if (!onTyp) return;
          const t: KontaktTyp = v === TYP_LABEL.firma ? "firma" : "person";
          // Auch gegen eine festlegende Rolle wechselbar (User 02.10.2026):
          // Rollen, die nur zum alten Typ passen, verwirft der Aufrufer
          // (`rollenFuerTyp`).
          onTyp(t);
        }}
        ariaLabel={TYP_FRAGE}
      />
      {typAusRolle && !typ && <span className={s.hinweis}>Aus der gewählten Rolle abgeleitet.</span>}
      {!typ && !typAusRolle && (
        <span className={s.hinweis}>Das Formular erscheint, sobald Firma oder Privat gewählt ist.</span>
      )}
    </div>
  );

  return (
    <div className={s.schritt}>
      <StatusSegmented
        value={kontaktStatus}
        options={ANLAGE_STATUS}
        onChange={statusGewaehlt}
        ariaLabel="Kontaktstatus"
      />
      {sonstigeBlock}
      {!convert && (onTyp || typ) && typBlock}
      {children}
    </div>
  );
}
